import {
  ListEntityTagsResponse,
  ListNamespacesRequest,
  ListNamespacesResponse,
  ReportEntityTagHistoryRequest,
  ReportEntityTagHistoryResponse,
  ReportEntityTagStateRequest,
  ReportEntityTagStateResponse,
  ListEntityTagsRequest,
  CreateEntityTagRequest,
  DeleteEntityTagRequest,
  ReportEntityTagsStateRequest,
  ReportEntityTagsStateResponse,
  ListTagsRequest,
  ListTagsResponse,
  GetTagRequest,
  Tag,
  CreateTagRequest,
  DeleteTagRequest,
  DeleteTagResponse,
  CreateNamespaceRequest,
  Namespace,
  ReportTagStatsRequest,
  ReportTagStatsResponse,
  ChangeTagPercentageRequest,
  ChangeTagPercentageResponse,
  API,
  WipeTagRequest,
  WipeTagResponse,
  QueryNamespaceRequest,
  QueryNamespaceResponse,
} from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';
import { CustomAxios, getResponseData } from 'redux/axios';
import { REACT_APP_API_SCHEMAS_URL } from 'config/app';

const BASE_URL = REACT_APP_API_SCHEMAS_URL;

export const getNamespaces = async (req: ListNamespacesRequest) => {
  const response = await CustomAxios.get<ListNamespacesResponse>(
    `/wetags/v1/namespaces`,
    {
      params: {
        page_size: req.pageSize,
        page_token: req.pageToken,
        filter: req.filter,
      },
      baseURL: BASE_URL,
    }
  );

  return getResponseData<ListNamespacesResponse>(response);
};

export const createNamespace = async (
  req: CreateNamespaceRequest
): Promise<Namespace> => {
  const response = await CustomAxios.post<Namespace>(`/wetags/v1/namespaces`, req, {
    baseURL: BASE_URL,
  });

  return getResponseData<Namespace>(response);
};

export const getTags = async (req: ListTagsRequest) => {
  const response = await CustomAxios.get<ListTagsResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}/tags`,
    {
      params: {
        page_size: req.pageSize,
        page_token: req.pageToken,
        filter: req.filter,
      },
      baseURL: BASE_URL,
    }
  );

  return getResponseData<ListTagsResponse>(response);
};

export const getTag = async (req: GetTagRequest) => {
  const response = await CustomAxios.get<ListTagsResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}/tags/${req.tagId}`,
    {
      baseURL: BASE_URL,
    }
  );

  return getResponseData<Tag>(response);
};

export const reportEntityTagsState = async (req: ReportEntityTagsStateRequest) => {
  const response = await CustomAxios.get<ReportEntityTagsStateResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}:reportEntityTagsState`,
    {
      params: {
        entity_id: req.entityId,
        page_size: req.pageSize,
        page_token: req.pageToken,
        filter_tags: req.filterTags,
      },
      baseURL: BASE_URL,
    }
  );

  return getResponseData<ReportEntityTagsStateResponse>(response);
};

export const reportEntityTagState = async (req: ReportEntityTagStateRequest) => {
  const response = await CustomAxios.get<ReportEntityTagStateResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}/tags/${req.tagId}:reportEntityTagState`,
    {
      params: {
        entity_id: req.entityId,
      },
      baseURL: BASE_URL,
    }
  );

  return getResponseData<ReportEntityTagStateResponse>(response);
};

export const reportEntityTagHistory = async (req: ReportEntityTagHistoryRequest) => {
  const response = await CustomAxios.get<ReportEntityTagHistoryResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}/tags/${req.tagId}:reportEntityTagHistory`,
    {
      params: {
        entity_id: req.entityId,
        page_size: req.pageSize,
        page_token: req.pageToken,
        filter: req.filter,
        format: req.format,
      },
      baseURL: BASE_URL,
    }
  );

  return getResponseData<ReportEntityTagHistoryResponse>(response);
};

export const listEntityTags = async (req: ListEntityTagsRequest) => {
  const response = await API.ListEntityTags(
    async (url) =>
      getResponseData<ListEntityTagsResponse>(
        await CustomAxios.get<ListEntityTagsResponse>(url, {
          baseURL: BASE_URL,
        })
      ),
    req
  );

  return response;
};

export const createEntityTag = async (req: CreateEntityTagRequest): Promise<void> => {
  await CustomAxios.post(
    `/wetags/v1/namespaces/${req.entityTag?.namespaceId}/tags/${req.entityTag?.tagId}/entityTags`,
    req.entityTag,
    {
      baseURL: BASE_URL,
    }
  );
};

export const deleteEntityTag = async (req: DeleteEntityTagRequest): Promise<void> => {
  await CustomAxios.delete(
    `/wetags/v1/namespaces/${req.namespaceId}/tags/${req.tagId}/entityTags/${req.entityId}`,
    {
      baseURL: BASE_URL,
    }
  );
};

export const createTag = async (req: CreateTagRequest): Promise<Tag> => {
  const response = await CustomAxios.post<Tag>(
    `/wetags/v1/namespaces/${req.tag?.namespaceId}/tags`,
    req.tag,
    {
      baseURL: BASE_URL,
    }
  );

  return getResponseData<Tag>(response);
};

export const deleteTag = async (req: DeleteTagRequest): Promise<DeleteTagResponse> => {
  const response = await CustomAxios.delete<DeleteTagResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}/tags/${req.tagId}`,
    {
      baseURL: BASE_URL,
    }
  );

  return getResponseData<DeleteTagResponse>(response);
};

export const getTagStats = async (req: ReportTagStatsRequest) => {
  const response = await CustomAxios.get<ReportTagStatsResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}/tags/${req.tagId}:reportTagStats`,
    {
      baseURL: BASE_URL,
    }
  );

  // schema says it's a number but it's returned as a string, so convert it here
  // this is due to int64 handling in protobuf or grpc over JSON
  const responseData = getResponseData<ReportTagStatsResponse>(response);
  responseData.explicitlyTaggedEntitiesCount = parseInt(
    responseData.explicitlyTaggedEntitiesCount as any
  );
  return responseData;
};

export const changeTagPercentage = async (
  req: ChangeTagPercentageRequest
): Promise<ChangeTagPercentageResponse> => {
  const response = await CustomAxios.post<ChangeTagPercentageResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}/tags/${req.tagId}:changeTagPercentage`,
    req,
    {
      baseURL: BASE_URL,
    }
  );

  return getResponseData<ChangeTagPercentageResponse>(response);
};

export const queryNamespace = async (
  req: QueryNamespaceRequest
): Promise<QueryNamespaceResponse> => {
  const response = await CustomAxios.post<QueryNamespaceResponse>(
    `/wetags/v1/namespaces/${req.namespaceId}:queryNamespace`,
    req,
    {
      baseURL: BASE_URL,
    }
  );

  return getResponseData<QueryNamespaceResponse>(response);
};

export const wipeTag = async (req: WipeTagRequest) => {
  const response = await API.WipeTag(
    async (url) =>
      getResponseData<WipeTagResponse>(
        await CustomAxios.post<WipeTagResponse>(url, req, {
          baseURL: BASE_URL,
        })
      ),
    req
  );

  return response;
};
